import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';

import { mapLinkDefault, mapLinkMobile } from '@/helpers/location-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { getCountryFromCode } from '@cineamo/ui/src/helpers/international-helper';
import { OS } from '@cineamo/ui/src/helpers/os-helper';

import { IconNavigation16 } from '@cineamo/ui/src/icons/ic-navigation';

export const CINEMA_ID_PLACEHOLDER = 'name-of-cinema';

export function cinemaIdOrSlug(cinema?: CinemaDto): number | string {
    return cinema?.slug || cinema?.id || CINEMA_ID_PLACEHOLDER;
}

export function checkForCinemaPlaceholderOrUndefined(cinemaIdOrSlug: number | string): boolean {
    return !cinemaIdOrSlug || cinemaIdOrSlug === CINEMA_ID_PLACEHOLDER;
}

export function constructCinemaAddress(cinema: CinemaDto): string {
    return `${cinema.street && cinema.street}${cinema.postalCode && `, ${cinema.postalCode}`}
        ${cinema.city && ` ${cinema.city}`}`;
}

export function getCinemaInitials(cinema: CinemaDto): string {
    const cinemaName = cinema?.name || '';
    const initials: string[] = [];
    if (cinemaName.length > 2) {
        let textParts: string[] = cinemaName.split(' ');
        if (textParts.length <= 1) {
            textParts = cinemaName.split('-');
        }
        if (textParts.length >= 1) {
            const firstInitial = textParts[0]?.[0];
            if (firstInitial) {
                initials.push(firstInitial);
            }
            if (textParts.length >= 2) {
                const secondInitial = textParts[1]?.[0];
                if (secondInitial) {
                    initials.push(secondInitial);
                }
            }
        }
    } else if (cinemaName.length > 0) {
        if (cinemaName[0]) {
            initials.push(cinemaName[0]);
        }
        if (cinemaName[1]) {
            initials.push(cinemaName[1]);
        }
    }
    return initials.join('').toUpperCase();
}

export function getCinemaLogo(cinema: CinemaDto, useProfileImageAsFallback = false): string | undefined {
    const logo = cinema?.logoImageUrl;
    if (!logo && useProfileImageAsFallback) {
        return cinema?.profileImageUrl;
    }
    return logo;
}

export function jsxMapLinkMobile(options: {
    os: OS;
    cinema: CinemaDto;
    style?: {
        classNameLabel?: string;
    };
    locale: string;
}) {
    const cinema = options.cinema;
    const fullAddressParts: string[] = [];
    const postalCodeCityParts: string[] = [];
    if (cinema.street) {
        fullAddressParts.push(cinema.street);
    }
    if (cinema.postalCode) {
        postalCodeCityParts.push(cinema.postalCode);
    }
    if (cinema.city) {
        postalCodeCityParts.push(cinema.city);
    }
    fullAddressParts.push(postalCodeCityParts.join(' '));
    if (cinema.countryCode) {
        fullAddressParts.push(cinema.countryCode);
    }
    const fullAddress = fullAddressParts.join(', ');
    return (
        <a
            className="w-full flex flex-row space-x-8 items-center overflow-hidden"
            href={mapLinkMobile({
                street: cinema.street,
                postalCode: cinema.postalCode,
                city: cinema.city,
                os: options.os
            })}>
            <div className={cn('flex flex-row overflow-hidden', options?.style?.classNameLabel)}>
                {fullAddress && <span className="truncate">{fullAddress}</span>}
            </div>
            <IconNavigation16 className="flex-none text-[var(--primary-color)] w-[12px] h-[12px]" />
        </a>
    );
}

export function jsxMapLinkDefault(options: {
    cinema: CinemaDto;
    style?: {
        classNameLabel?: string;
    };
    locale: string;
}) {
    const cinema = options.cinema;
    const locale = options.locale;
    const fullAddressParts: string[] = [];
    const postalCodeCityParts: string[] = [];
    if (cinema.street) {
        fullAddressParts.push(cinema.street);
    }
    if (cinema.postalCode) {
        postalCodeCityParts.push(cinema.postalCode);
    }
    if (cinema.city) {
        postalCodeCityParts.push(cinema.city);
    }
    fullAddressParts.push(postalCodeCityParts.join(' '));

    const country = cinema.countryCode ? getCountryFromCode(cinema.countryCode, locale) : null;
    if (country) {
        fullAddressParts.push(country);
    }
    const fullAddress = fullAddressParts.join(', ');
    return (
        <a
            className="flex flex-row space-x-8 items-center"
            href={mapLinkDefault({
                street: cinema.street,
                postalCode: cinema.postalCode,
                city: cinema.city
            })}>
            <div className={cn('flex flex-row', options?.style?.classNameLabel)}>
                {fullAddress && <span>{fullAddress}</span>}
            </div>
            <IconNavigation16 className="flex-none text-[var(--primary-color)]" />
        </a>
    );
}

export function getDeterministicColorFromString(text: string): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
        const char = text.charCodeAt(i);
        hash = char + ((hash << 5) - hash);
    }

    const r = hash & 0xff;
    const g = (hash >> 8) & 0xff;
    const b = (hash >> 16) & 0xff;

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
